import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Flex, Switch, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'

import { ProTableContainer, QueryErrorAlert } from 'src/components'
import {
  NOTIFICATION_CHANNELS,
  PREFERENCE_ROWS,
  type DisabledCategoriesMatrix,
  type NotificationChannel,
  type PreferenceRow,
  fetchNotificationPreferences,
  updateNotificationPreferences
} from 'src/features/account/api/notificationPreferences'
import { useShowApiErrors } from 'src/utils'

const { Text } = Typography

const PREFERENCES_QUERY_KEY = ['account', 'notification-preferences'] as const

const isChannelEnabled = (
  matrix: DisabledCategoriesMatrix | undefined,
  row: PreferenceRow,
  channel: NotificationChannel
): boolean => {
  const disabled = matrix?.[row.appCode]?.[row.category] ?? []

  return !disabled.includes(channel)
}

const toggleInMatrix = (
  matrix: DisabledCategoriesMatrix,
  row: PreferenceRow,
  channel: NotificationChannel,
  enabled: boolean
): DisabledCategoriesMatrix => {
  const appBucket = { ...(matrix[row.appCode] ?? {}) }
  const current = new Set(appBucket[row.category] ?? [])
  if (enabled) current.delete(channel)
  else current.add(channel)
  appBucket[row.category] = Array.from(current) as NotificationChannel[]
  return { ...matrix, [row.appCode]: appBucket }
}

export const AccountNotificationsPanel = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()

  const preferencesQuery = useQuery({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: async () => {
      const response = await fetchNotificationPreferences()

      return response.data?.disabled_categories ?? {}
    }
  })

  const matrix = preferencesQuery.data ?? {}

  const updateMutation = useMutation({
    mutationFn: updateNotificationPreferences,
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_QUERY_KEY })
      const previous = queryClient.getQueryData<DisabledCategoriesMatrix>(
        PREFERENCES_QUERY_KEY
      )
      queryClient.setQueryData(PREFERENCES_QUERY_KEY, next)
      return { previous }
    },
    onError: (error, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(PREFERENCES_QUERY_KEY, ctx.previous)
      }
      showApiErrors(error)
    },
    onSuccess: (response) => {
      const next = response.data?.disabled_categories ?? {}
      queryClient.setQueryData(PREFERENCES_QUERY_KEY, next)
      message.success(
        t('account.notifications.preferences.saveSuccess', {
          defaultValue: 'Preferences saved'
        })
      )
    }
  })

  const handleToggle = (
    row: PreferenceRow,
    channel: NotificationChannel,
    enabled: boolean
  ) => {
    const next = toggleInMatrix(matrix, row, channel, enabled)
    updateMutation.mutate(next)
  }

  const channelColumns: ColumnsType<PreferenceRow> = NOTIFICATION_CHANNELS.map(
    (channel) => ({
      key: channel,
      title: t(`account.notifications.preferences.channels.${channel}`, {
        defaultValue: channel
      }),
      align: 'center',
      width: 110,
      render: (_value, row) => {
        if (!row.channels.includes(channel)) {
          return (
            <Text type="secondary" aria-label="not applicable">
              —
            </Text>
          )
        }
        return (
          <Switch
            checked={isChannelEnabled(matrix, row, channel)}
            loading={preferencesQuery.isLoading || updateMutation.isPending}
            disabled={preferencesQuery.isError}
            onChange={(checked) => handleToggle(row, channel, checked)}
          />
        )
      }
    })
  )

  const preferenceColumns: ColumnsType<PreferenceRow> = [
    {
      key: 'category',
      fixed: 'left',
      title: t('account.notifications.preferences.categoryColumn', {
        defaultValue: 'Category'
      }),
      render: (_value, row) => (
        <Text>{t(`account.notifications.preferences.rows.${row.key}`)}</Text>
      )
    },
    ...channelColumns
  ]

  return (
    <Flex vertical gap={16}>
      {preferencesQuery.isError ? (
        <QueryErrorAlert onRetry={() => void preferencesQuery.refetch()} />
      ) : null}

      <ProTableContainer<PreferenceRow>
        showSearch={false}
        rowKey="key"
        columns={preferenceColumns}
        dataSource={PREFERENCE_ROWS}
        pagination={false}
        loading={preferencesQuery.isLoading}
        leftChildren={
          <Text strong>
            {t('account.notifications.preferences.title', {
              defaultValue: 'Preferences'
            })}
          </Text>
        }
      />
    </Flex>
  )
}
